import { Tooltip } from '@material-ui/core'
import Icon from '@material-ui/core/Icon'
import IconButton from '@material-ui/core/IconButton'
import { MUIDataTableColumnDef } from 'mui-datatables'
import React, { Fragment, useState } from 'react'
import { EnumUser } from '../../../../../graphql/typings/global_types'
import DataTable from '../../../../components/DataTable'
import { ConfirmDialog } from '../../../../components/Dialog'
import Panel from '../../../../components/Panel'
import { Insurance_Minimal } from '../../../../helpers/data/models/contract/Insurance'
import { useErrorService } from '../../../../helpers/errors/ErrorService'
import IconHelper from '../../../../helpers/IconHelper'
import { useAuthUser } from '../../../../hooks/useAuth'
import { useCommonDataLoadedExisting } from '../../../../hooks/useCommonData'
import { useDeleteInsurance } from '../../../../hooks/useMutations'
import useSnackBar from '../../../../hooks/useSnackBar'
import InsuranceDialog from './InsuranceDialog'

type DialogState =
    | {
          open: true
          insurance: Insurance_Minimal
      }
    | { open: false }

const AEAInsurancesList = () => {
    const { insurances } = useCommonDataLoadedExisting()
    const { errorAlert } = useErrorService()
    const auth = useAuthUser()
    const isAdmin = auth.user.userType === EnumUser.admin
    const { openSnack } = useSnackBar()
    const deleteInsuranceMutation = useDeleteInsurance()

    const [openAddDialog, setOpenAddDialog] = useState<boolean>(false)
    const [openEditDialog, setOpenEditDialog] = useState<DialogState>({ open: false })
    const [openDeleteDialog, setOpenDeleteDialog] = useState<DialogState>({
        open: false,
    })
    const [deleteLoading, setDeleteLoading] = useState<boolean>(false)

    const columns: MUIDataTableColumnDef[] = [
        {
            name: 'title',
            label: 'Nom',
        },
        {
            name: 'phone',
            label: 'Téléphone',
        },
        {
            name: 'email',
            label: 'Email',
        },
    ]

    const addFeatureData = {
        addHandler: () => {
            setOpenAddDialog(true)
        },
        tooltip: `Ajout d'une assurance`,
    }

    const onCreated = (insurance: Insurance_Minimal) => {
        openSnack({
            type: 'success',
            message: `Assurance ${insurance.title} créée avec succès !`,
        })
    }

    const onEdited = (insurance: Insurance_Minimal) => {
        openSnack({
            message: 'Assurance modifiée avec succès !',
            type: 'success',
        })
    }

    const onEditClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, insurance: Insurance_Minimal) => {
        event.stopPropagation()
        setOpenEditDialog({ open: true, insurance })
    }

    const onDeleteClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, insurance: Insurance_Minimal) => {
        event.stopPropagation()
        setOpenDeleteDialog({ open: true, insurance })
    }

    const onDeleteConfirmed = async () => {
        if (!openDeleteDialog.open) return

        setDeleteLoading(true)

        try {
            const insurance = openDeleteDialog.insurance

            const response = await deleteInsuranceMutation.run({
                id: insurance.id,
            })

            if (response.errors.length > 0) {
                throw "Une erreur est survenue pendant la suppression de l'assurance."
            } else {
                openSnack({
                    message: 'Assurance supprimée avec succès !',
                    type: 'success',
                })
            }
        } catch (error) {
            errorAlert(error)
        } finally {
            setDeleteLoading(false)
            setOpenDeleteDialog({ open: false })
        }
    }

    const actionsColumn = (dataIndex: number, rowIndex: number) => {
        if (!isAdmin) return null

        const insurance = insurances[dataIndex]

        return (
            <div className="flex items-center">
                <div className="flex-grow"></div>
                <Tooltip title="Modifier l'assurance">
                    <IconButton onClick={(event) => onEditClick(event, insurance)}>
                        <Icon>edit</Icon>
                    </IconButton>
                </Tooltip>
                <Tooltip title="Supprimer l'assurance">
                    <IconButton onClick={(event) => onDeleteClick(event, insurance)}>
                        <Icon>delete</Icon>
                    </IconButton>
                </Tooltip>
            </div>
        )
    }

    return (
        <Fragment>
            <Panel>
                <DataTable
                    title={'Assurances'}
                    icon={IconHelper.elementIcons.insuranceIcon}
                    data={insurances}
                    columns={columns}
                    showSearch={true}
                    actionsColumn={actionsColumn}
                    addFeature={addFeatureData}
                    options={{
                        sortOrder: {
                            name: 'title',
                            direction: 'asc',
                        },
                    }}
                />
            </Panel>
            <InsuranceDialog onDone={onCreated} open={openAddDialog} closeDialog={() => setOpenAddDialog(false)} />
            {openEditDialog.open && (
                <InsuranceDialog
                    onDone={onEdited}
                    open={openEditDialog.open}
                    closeDialog={() => setOpenEditDialog({ open: false })}
                    insurance={openEditDialog.insurance}
                />
            )}
            {openDeleteDialog.open && (
                <ConfirmDialog
                    open={openDeleteDialog.open}
                    onCancelled={() => setOpenDeleteDialog({ open: false })}
                    onConfirmed={onDeleteConfirmed}
                    title="Suppression d'une assurance"
                    text={`Voulez-vous vraiment supprimer l'assurance "${openDeleteDialog.insurance.title}" ?`}
                    loading={deleteLoading}
                />
            )}
        </Fragment>
    )
}

export default AEAInsurancesList
